import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * The order being edited in the Edit Order modal, and what its edits are measured against.
 *
 * `clone` is `{ ...order, ...overrides }`. `baseOrder` is the stored order the diffs at Done run
 * against: the prop, or the reloaded order after `replaceWith`. All three reset when `order`
 * changes identity.
 *
 * `modifyClone` and `setDirty` are the raw setters, for `useEditModalDynamicFields` and
 * `useEditModalCallbacks`, which take them as-is.
 *
 * @param {object} order The order prop.
 * @returns {{
 *   clone: object,
 *   overrides: object,
 *   baseOrder: object,
 *   isDirty: object,
 *   modifyClone: Function,
 *   setDirty: Function,
 *   replaceWith: (fresh: object) => void,
 *   patch: (fields: object, opts?: { stored?: boolean }) => void,
 * }}
 */
export const useOrderDraft = (order) => {
    const [overrides, modifyClone] = useState({});
    const [isDirty, setDirty] = useState({});
    const [baseOrder, setBaseOrder] = useState(order);

    const clone = useMemo(() => ({ ...order, ...overrides }), [order, overrides]);

    useEffect(() => {
        modifyClone({ ...(order || {}) });
        setDirty({});
        setBaseOrder(order);
    }, [order]);

    /**
     * Loads a freshly read order into the draft: the clone becomes `fresh`, dirty flags clear, and
     * the base is `{ ...order, ...fresh }` so a field the read doesn't return falls back to the
     * prop on both sides of Done's diffs.
     */
    const replaceWith = useCallback(
        (fresh) => {
            modifyClone({ ...fresh });
            setDirty({});
            setBaseOrder({ ...order, ...fresh });
        },
        [order]
    );

    /**
     * Merges `fields` into the clone, keeping edits in other tabs. With `{ stored: true }` they
     * also merge into `baseOrder`, so Done treats them as stored values rather than user edits.
     */
    const patch = useCallback((fields, { stored = false } = {}) => {
        modifyClone((prev) => ({ ...prev, ...fields }));
        if (stored) setBaseOrder((prev) => ({ ...prev, ...fields }));
    }, []);

    return { clone, overrides, baseOrder, isDirty, modifyClone, setDirty, replaceWith, patch };
};
